// Quote header · Urgency + Build Type strip
//
// Compact horizontal strip that consolidates the most important urgency
// signals AND the Build Type dropdown (with cross-sell + Hub popovers)
// into a single top row of primary controls.
//
// Layout (single row on ≥1440):
//   🏗 Build type [dropdown] · [CS] [HUB]  ·  📅 Target  ·  ⚡ ASAP  ·
//   🔥 Desperation [slider + pill]  ·  ⏳ Expires [date + pill]
//
// All controls autosave via the shared `onChange({...patch})` prop.

import { Calendar, Zap, Flame, Clock3, Truck } from "lucide-react";
import { T2Chip } from "../_t2";
import { urgencyBand } from "../_quoteHelpers";
import SupplierPanel from "./SupplierPanel";

export default function UrgencyHeader({ quote, onChange, testidPrefix = "urgency" }) {
  const timeline = quote.Timeline || { targetDate: null, asap: false, desperation: 0 };
  const desp = timeline.desperation ?? 0;
  const band = urgencyBand(desp, quote.Status === "Paid" || !!quote.Payment?.paid);
  const despTone  = band.tone;
  const despLabel = band.label;

  const exp = quote.ExpiryDate ? String(quote.ExpiryDate) : "";
  const expDate = exp ? new Date(exp) : null;
  const expDays = expDate && !isNaN(expDate)
    ? Math.ceil((expDate.getTime() - Date.now()) / (1000 * 60 * 60 * 24))
    : null;
  const expTone = expDays == null ? ""
    : expDays < 0 ? "red"
    : expDays < 2 ? "warn"
    : expDays < 7 ? "orange" : "";
  const expLabel = expDays == null ? "No expiry set"
    : expDays < 0 ? `Expired ${Math.abs(expDays)}d ago`
    : expDays === 0 ? "Expires today"
    : `Expires in ${expDays}d`;

  const patchTimeline = (patch) => onChange({ Timeline: { ...timeline, ...patch } });
  const patchExpiry   = (d)     => onChange({ ExpiryDate: d ? new Date(d).toISOString() : null });

  return (
    <div
      data-testid={`${testidPrefix}-strip`}
      className="t2-urgency-strip"
      style={{
        display: "flex", alignItems: "center", flexWrap: "wrap", gap: 18,
        margin: "12px 0 8px", padding: "12px 18px",
        borderRadius: 10,
        background: "linear-gradient(90deg, rgba(47, 200, 203, 0.05), rgba(255,255,255,0.02))",
        border: "1px solid rgba(47, 200, 203, 0.25)",
        boxShadow: "0 4px 24px -6px rgba(47, 200, 203, 0.12), 0 1px 0 rgba(255,255,255,0.04) inset",
      }}
    >
      {/* Supplier + rebate — primary control, leftmost (feeds the Margin summary live) */}
      <Field icon={Truck} label="Supplier" tone="teal">
        <SupplierPanel compact supplier={quote.Supplier} rebatePct={quote.RebatePct}
          onChange={({ Supplier, RebatePct }) => onChange({ Supplier, RebatePct })} />
      </Field>

      <Divider />

      {/* Target date + ASAP together */}
      <Field icon={Calendar} label="Target">
        <input
          type="date"
          className="t2-input t2-mono"
          style={{ padding: "4px 10px", fontSize: 13.5, minWidth: 155, height: 34 }}
          value={timeline.targetDate ? timeline.targetDate.slice(0, 10) : ""}
          disabled={!!timeline.asap}
          data-empty={(!timeline.targetDate && !timeline.asap) ? "true" : undefined}
          onChange={(e) => patchTimeline({ targetDate: e.target.value || null })}
          data-testid={`${testidPrefix}-target-date`}
        />
        <label style={{ display: "inline-flex", alignItems: "center", gap: 4, cursor: "pointer", marginLeft: 6 }}>
          <input
            type="checkbox"
            checked={!!timeline.asap}
            onChange={(e) => patchTimeline({
              asap: e.target.checked,
              targetDate: e.target.checked ? null : timeline.targetDate,
            })}
            data-testid={`${testidPrefix}-asap`}
          />
          <span
            className="t2-mono"
            style={{
              fontSize: 11.5, textTransform: "uppercase", letterSpacing: ".06em",
              color: timeline.asap ? "var(--t2-red-ink)" : "var(--t2-grey3)",
              display: "inline-flex", alignItems: "center", gap: 3,
            }}
          >
            <Zap className="w-3.5 h-3.5" /> ASAP
          </span>
        </label>
      </Field>

      <Divider />

      {/* Desperation slider */}
      <Field icon={Flame} label="Urgency">
        <input
          type="range" min="0" max="10" step="1"
          value={desp}
          onChange={(e) => patchTimeline({ desperation: Number(e.target.value), urgencyManual: true })}
          className="t2-slider"
          style={{ width: 140 }}
          data-testid={`${testidPrefix}-desperation`}
        />
        <T2Chip tone={despTone} className={`${band.anim || ""} qb-strip-chip`}>{band.key === "paid" ? despLabel : `${despLabel} · ${desp}/10`}</T2Chip>
      </Field>

      <Divider />

      {/* Expiry — date + pill */}
      <Field
        icon={Clock3}
        label="Expires"
        tooltip="New quotes default to +48h (Xero-style). Bump this when the deal warrants a longer runway."
      >
        <input
          type="date"
          className="t2-input t2-mono"
          style={{ padding: "4px 10px", fontSize: 13.5, minWidth: 155, height: 34 }}
          value={exp ? exp.slice(0, 10) : ""}
          data-empty={!exp ? "true" : undefined}
          onChange={(e) => patchExpiry(e.target.value)}
          data-testid={`${testidPrefix}-expiry`}
        />
        <T2Chip tone={expTone} className="qb-strip-chip">{expLabel}</T2Chip>
      </Field>
    </div>
  );
}


// ─── local UI primitives ───────────────────────────────────────────────
function Field({ icon: Icon, label, tone, tooltip, children }) {
  const iconColor = tone === "teal" ? "var(--t2-teal)" : "var(--t2-grey3)";
  const labelColor = tone === "teal" ? "var(--t2-teal)" : "var(--t2-grey3)";
  return (
    <div
      title={tooltip || undefined}
      style={{ display: "inline-flex", alignItems: "center", gap: 8 }}
    >
      <Icon className="w-4 h-4" style={{ color: iconColor }} />
      <span
        className="t2-mono"
        style={{
          fontSize: 11.5, textTransform: "uppercase", letterSpacing: ".06em",
          color: labelColor,
        }}
      >
        {label}
      </span>
      {children}
    </div>
  );
}

function Divider() {
  return (
    <span
      aria-hidden
      style={{
        width: 1, height: 30, background: "rgba(47, 200, 203, 0.15)",
      }}
    />
  );
}
